import React, { useState, useEffect } from 'react';
import { X, AlertCircle, Calendar, FileText, Info, CheckCircle, XCircle } from 'lucide-react';
import {
  BENEFICIARY_STATUS,
  STATUS_LABELS,
  STATUS_COLORS,
  SUSPENSION_REASONS,
  ARCHIVE_REASONS,
  ALLOWED_TRANSITIONS,
  getStatusLabel
} from '../../constants/beneficiaryStatuses';
import {
  validateStatusChange,
  getStatusChangeWarning,
  daysUntil
} from '../../utils/statusValidations';

/**
 * Modal de changement de statut pour les bénéficiaires
 *
 * @param {object} props - Props du composant
 * @param {boolean} props.isOpen - État d'ouverture du modal
 * @param {Function} props.onClose - Fonction de fermeture
 * @param {object} props.beneficiary - Données du bénéficiaire
 * @param {Function} props.onConfirm - Fonction de confirmation (newStatus, data)
 * @param {string} props.currentStatus - Statut actuel
 */
const ChangeBeneficiaryStatusModal = ({
  isOpen,
  onClose,
  beneficiary,
  onConfirm,
  currentStatus
}) => {
  const [selectedStatus, setSelectedStatus] = useState('');
  const [reason, setReason] = useState('');
  const [customReason, setCustomReason] = useState('');
  const [notes, setNotes] = useState('');
  const [endDate, setEndDate] = useState('');
  const [archiveDate, setArchiveDate] = useState('');
  const [deleteAppointments, setDeleteAppointments] = useState(true); // Pour l'archivage
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState([]);
  const [warning, setWarning] = useState('');

  // Réinitialiser le formulaire à l'ouverture
  useEffect(() => {
    if (isOpen) {
      setSelectedStatus('');
      setReason('');
      setCustomReason('');
      setNotes('');
      setEndDate('');
      setArchiveDate('');
      setErrors([]);
      setWarning('');

      // Pré-remplir la date d'archivage avec aujourd'hui
      const today = new Date().toISOString().split('T')[0];
      setArchiveDate(today);
    }
  }, [isOpen]);

  // Mettre à jour l'avertissement quand le statut change
  useEffect(() => {
    if (selectedStatus && currentStatus) {
      const warningMsg = getStatusChangeWarning(currentStatus, selectedStatus, {
        hasUpcomingAppointments: beneficiary?.hasUpcomingAppointments || false,
        hasUnpaidInvoices: beneficiary?.hasUnpaidInvoices || false
      });
      setWarning(warningMsg || '');
    } else {
      setWarning('');
    }
  }, [selectedStatus, currentStatus, beneficiary]);

  // Obtenir les statuts autorisés depuis le statut actuel
  // Note: ARCHIVED n'est disponible que pour la migration (archivé → archivé)
  const allowedStatuses = ALLOWED_TRANSITIONS[currentStatus] || [];

  // Gérer la soumission
  const handleSubmit = async (e) => {
    e.preventDefault();
    setErrors([]);

    // Validation
    const data = {
      reason: reason === 'autre' ? customReason : reason,
      customReason: reason === 'autre' ? customReason : '',
      notes,
      endDate: endDate ? new Date(endDate) : null,
      archiveDate: archiveDate ? new Date(archiveDate) : null,
      deleteAppointments: deleteAppointments // Ajouter l'option de suppression des RDV
    };

    const validation = validateStatusChange(currentStatus, selectedStatus, data);

    if (!validation.valid) {
      setErrors(validation.errors);
      return;
    }

    // Confirmation
    setLoading(true);

    try {
      await onConfirm(selectedStatus, data);
      onClose();
    } catch (error) {
      setErrors([error.message || 'Erreur lors du changement de statut']);
    } finally {
      setLoading(false);
    }
  };

  // Obtenir les raisons selon le statut sélectionné
  const getReasonOptions = () => {
    if (selectedStatus === BENEFICIARY_STATUS.SUSPENDED) {
      return SUSPENSION_REASONS;
    } else if (selectedStatus === BENEFICIARY_STATUS.ARCHIVED) {
      return ARCHIVE_REASONS;
    }
    return [];
  };

  // Vérifier si des champs supplémentaires sont nécessaires
  const requiresReason = [BENEFICIARY_STATUS.SUSPENDED, BENEFICIARY_STATUS.ARCHIVED].includes(selectedStatus);
  const requiresEndDate = selectedStatus === BENEFICIARY_STATUS.SUSPENDED;
  const requiresArchiveDate = selectedStatus === BENEFICIARY_STATUS.ARCHIVED;

  // Calculer les jours jusqu'à la date de fin (pour suspension)
  const daysRemaining = endDate ? daysUntil(new Date(endDate)) : null;

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        {/* En-tête */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200 dark:border-gray-700">
          <div>
            <h2 className="text-xl font-semibold text-gray-900 dark:text-white">
              Changer le statut du bénéficiaire
            </h2>
            <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
              {beneficiary?.nom || 'Bénéficiaire'}
            </p>
          </div>
          <button
            onClick={onClose}
            disabled={loading}
            className="text-gray-400 hover:text-gray-500 dark:hover:text-gray-300"
          >
            <X className="w-6 h-6" />
          </button>
        </div>

        <form onSubmit={handleSubmit}>
          <div className="p-6 space-y-6">
            {/* Statut actuel */}
            <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-4">
              <div className="flex items-center gap-2">
                <Info className="w-5 h-5 text-blue-600 dark:text-blue-400" />
                <span className="text-sm font-medium text-blue-900 dark:text-blue-100">
                  Statut actuel :
                </span>
                <span className={`px-2 py-1 rounded-full text-xs font-medium ${STATUS_COLORS[currentStatus]?.bg} ${STATUS_COLORS[currentStatus]?.text}`}>
                  {getStatusLabel(currentStatus)}
                </span>
              </div>
            </div>

            {/* Sélection du nouveau statut */}
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Nouveau statut <span className="text-red-500">*</span>
              </label>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                {allowedStatuses.map(status => {
                  const colors = STATUS_COLORS[status];
                  const label = getStatusLabel(status);

                  return (
                    <label
                      key={status}
                      className={`
                        relative flex items-center p-4 border-2 rounded-lg cursor-pointer transition-all
                        ${selectedStatus === status
                          ? `${colors.border} ${colors.bg}`
                          : 'border-gray-200 dark:border-gray-700 hover:border-gray-300 dark:hover:border-gray-600'
                        }
                      `}
                    >
                      <input
                        type="radio"
                        name="status"
                        value={status}
                        checked={selectedStatus === status}
                        onChange={(e) => setSelectedStatus(e.target.value)}
                        className="sr-only"
                        disabled={loading}
                      />
                      <div className="flex items-center justify-between w-full">
                        <span className={`font-medium ${selectedStatus === status ? colors.text : 'text-gray-700 dark:text-gray-300'}`}>
                          {label}
                        </span>
                        {selectedStatus === status && (
                          <CheckCircle className={`w-5 h-5 ${colors.text}`} />
                        )}
                      </div>
                    </label>
                  );
                })}
              </div>

              {allowedStatuses.length === 0 && (
                <div className="text-sm text-gray-500 dark:text-gray-400 mt-2">
                  Aucune transition de statut disponible
                </div>
              )}
            </div>

            {/* Message spécial pour ré-archivage */}
            {currentStatus === 'archive' && selectedStatus === 'archive' && (
              <div className="bg-blue-50 dark:bg-blue-900/20 border-l-4 border-blue-500 p-4 rounded">
                <div className="flex items-start gap-2">
                  <Info className="w-5 h-5 text-blue-600 dark:text-blue-400 flex-shrink-0 mt-0.5" />
                  <div className="text-sm text-blue-800 dark:text-blue-200">
                    <strong>Migration depuis l'ancien système</strong><br />
                    Ce bénéficiaire est déjà archivé. Vous pouvez mettre à jour la raison, la date et ajouter des notes pour le nouveau système de statuts.
                  </div>
                </div>
              </div>
            )}

            {/* Avertissement */}
            {warning && selectedStatus !== currentStatus && (
              <div className="bg-yellow-50 dark:bg-yellow-900/20 border-l-4 border-yellow-500 p-4 rounded">
                <div className="flex items-start gap-2">
                  <AlertCircle className="w-5 h-5 text-yellow-600 dark:text-yellow-400 flex-shrink-0 mt-0.5" />
                  <div className="text-sm text-yellow-800 dark:text-yellow-200 whitespace-pre-line">
                    {warning}
                  </div>
                </div>
              </div>
            )}

            {/* Champs spécifiques selon le statut */}
            {selectedStatus && (
              <div className="space-y-4 pt-4 border-t border-gray-200 dark:border-gray-700">
                {/* Raison (pour suspension et archivage) */}
                {requiresReason && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Raison <span className="text-red-500">*</span>
                    </label>
                    <select
                      value={reason}
                      onChange={(e) => setReason(e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white"
                      disabled={loading}
                      required
                    >
                      <option value="">Sélectionner une raison...</option>
                      {getReasonOptions().map(opt => (
                        <option key={opt.value} value={opt.value}>
                          {opt.label}
                        </option>
                      ))}
                    </select>
                  </div>
                )}

                {/* Raison personnalisée si "autre" */}
                {reason === 'autre' && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Précisez la raison <span className="text-red-500">*</span>
                    </label>
                    <textarea
                      value={customReason}
                      onChange={(e) => setCustomReason(e.target.value)}
                      rows={3}
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white"
                      placeholder="Décrivez la raison (minimum 10 caractères)..."
                      disabled={loading}
                      required
                      minLength={10}
                    />
                    <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                      {customReason.length} / 10 caractères minimum
                    </p>
                  </div>
                )}

                {/* Date de fin (pour suspension) */}
                {requiresEndDate && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2 flex items-center gap-2">
                      <Calendar className="w-4 h-4" />
                      Date de fin de suspension <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="date"
                      value={endDate}
                      onChange={(e) => setEndDate(e.target.value)}
                      min={new Date(Date.now() + 86400000).toISOString().split('T')[0]}
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white"
                      disabled={loading}
                      required
                    />
                    {daysRemaining !== null && daysRemaining >= 0 && (
                      <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                        Suspension de {daysRemaining} jour{daysRemaining > 1 ? 's' : ''} (réactivation automatique)
                      </p>
                    )}
                  </div>
                )}

                {/* Date d'archivage */}
                {requiresArchiveDate && (
                  <>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2 flex items-center gap-2">
                        <Calendar className="w-4 h-4" />
                        Date d'effet de l'archivage <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="date"
                        value={archiveDate}
                        onChange={(e) => setArchiveDate(e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white"
                        disabled={loading}
                        required
                      />
                      <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                        Date passée ou future - Les rendez-vous à partir de cette date pourront être supprimés
                      </p>
                    </div>

                    {/* Option de suppression des rendez-vous */}
                    <div className="flex items-start">
                      <input
                        type="checkbox"
                        id="deleteAppointments"
                        checked={deleteAppointments}
                        onChange={(e) => setDeleteAppointments(e.target.checked)}
                        className="h-4 w-4 mt-1 text-blue-600 focus:ring-blue-500 border-gray-300 dark:border-gray-600 rounded"
                        disabled={loading}
                      />
                      <label htmlFor="deleteAppointments" className="ml-2 block text-sm text-gray-700 dark:text-gray-300">
                        Supprimer tous les rendez-vous à partir de la date choisie
                      </label>
                    </div>

                    {/* Avertissement suppression RDV */}
                    {deleteAppointments && archiveDate && (
                      <div className="bg-yellow-50 dark:bg-yellow-900/20 border-l-4 border-yellow-500 p-4 rounded">
                        <div className="flex items-start gap-2">
                          <AlertCircle className="w-5 h-5 text-yellow-600 dark:text-yellow-400 flex-shrink-0 mt-0.5" />
                          <div className="text-sm text-yellow-800 dark:text-yellow-200">
                            <strong>Attention :</strong> Tous les rendez-vous de ce bénéficiaire à partir du{' '}
                            {new Date(archiveDate).toLocaleDateString('fr-FR')} seront <strong>définitivement supprimés</strong>.
                          </div>
                        </div>
                      </div>
                    )}
                  </>
                )}

                {/* Notes additionnelles */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2 flex items-center gap-2">
                    <FileText className="w-4 h-4" />
                    Notes additionnelles (optionnel)
                  </label>
                  <textarea
                    value={notes}
                    onChange={(e) => setNotes(e.target.value)}
                    rows={3}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white"
                    placeholder="Ajoutez des notes supplémentaires si nécessaire..."
                    disabled={loading}
                  />
                </div>
              </div>
            )}

            {/* Erreurs de validation */}
            {errors.length > 0 && (
              <div className="bg-red-50 dark:bg-red-900/20 border-l-4 border-red-500 p-4 rounded">
                <div className="flex items-start gap-2">
                  <XCircle className="w-5 h-5 text-red-600 dark:text-red-400 flex-shrink-0 mt-0.5" />
                  <div className="flex-1">
                    <h3 className="text-sm font-medium text-red-800 dark:text-red-200 mb-1">
                      Erreurs de validation :
                    </h3>
                    <ul className="list-disc list-inside text-sm text-red-700 dark:text-red-300 space-y-1">
                      {errors.map((error, index) => (
                        <li key={index}>{error}</li>
                      ))}
                    </ul>
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Boutons d'action */}
          <div className="flex items-center justify-end gap-3 px-6 py-4 bg-gray-50 dark:bg-gray-900/50 border-t border-gray-200 dark:border-gray-700 rounded-b-lg">
            <button
              type="button"
              onClick={onClose}
              disabled={loading}
              className="px-4 py-2 text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors disabled:opacity-50"
            >
              Annuler
            </button>
            <button
              type="submit"
              disabled={loading || !selectedStatus}
              className="px-6 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
            >
              {loading ? (
                <>
                  <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                  Changement en cours...
                </>
              ) : (
                <>
                  <CheckCircle className="w-4 h-4" />
                  Confirmer le changement
                </>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default ChangeBeneficiaryStatusModal;
